import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { dismissUndoNotification } from 'owa-calendar-actions';
import { fetchFullCalendarEvent } from 'owa-calendar-forms-common/lib/helpers/eventStore';
import { isBirthdayEvent, isEventAMeeting } from 'owa-calendar-event-capabilities';
import { isCalendarEventLocalLie } from 'owa-calendar-event-local-lie';
import { returnTopExecutingActionDatapoint } from 'owa-analytics';
import { lazyInitializeLivePersonaCardForBirthdayEvent } from 'owa-event-context-menu/lib/lazyFunctions';
import { setPeekState } from '../actions/itemPeekActions';
import { showItemPeek } from '../actions/showItemPeek';
import { hideItemPeek } from '../actions/hideItemPeek';
import { NotesEditorViewState, lazyOpenNotesEditor } from 'owa-calendar-notes-editor';
import {
    default as getTimeAsPastPresentFutureType,
    PastPresentFutureType,
} from '../utils/getTimeAsPastPresentFutureType';
import { default as getTimeRelevance } from 'owa-datetime-utils/lib/getTimeRelevance';
import { lazyFetchMobilityBookends } from 'owa-mobility-read-view';
import { isConnectedAccount } from 'owa-accounts-store';
import { isBusiness } from 'owa-session-store';
import { isFeatureEnabled } from 'owa-feature-flags';

/**
 * Open calendar item peek
 */
export async function openItemPeekInternal(
    item: CalendarEvent,
    notesEditorViewState?: NotesEditorViewState
) {
    const dataPoint = returnTopExecutingActionDatapoint();

    dismissUndoNotification();
    if (isCalendarEventLocalLie(item)) {
        return;
    }

    const { event, fullEventPromise } = fetchFullCalendarEvent(
        item.ItemId,
        undefined /** folderId */,
        undefined /** getExistingCalendarEvent */,
        undefined /** isfetchFullSeriesMasterCalendarEventFromEventInstance */,
        { fetchSource: 'OpenPeek' } /** customData */
    );

    // Call the dummy v2 actions so that the v2 stitches in quick compose and module command will be able to work
    hideItemPeek();
    showItemPeek();

    // don't wait for the fetch promise to return before triggering the peek, so that peek can be shown with
    // existing partial event in the cache and will get updated when full event is fetched and placed in cache.
    setPeekState({ itemId: item.ItemId.Id });
    if (notesEditorViewState) {
        lazyOpenNotesEditor.importAndExecute(
            item.ItemId,
            notesEditorViewState.eventScope,
            notesEditorViewState.onSend,
            notesEditorViewState.titleText,
            notesEditorViewState.iconName,
            notesEditorViewState.proposedTime,
            notesEditorViewState.forwardees,
            notesEditorViewState.showNotifyToggle,
            notesEditorViewState.sendResponse,
            notesEditorViewState.isCancelledEvent,
            notesEditorViewState.isDeclinedSeriesInstance,
            notesEditorViewState.isTxpShareEvent
        );
    }

    // if this a birthday event, initialise the LivePersonaCard in background (do not await) if it is not already initialized
    if (isBirthdayEvent(item)) {
        lazyInitializeLivePersonaCardForBirthdayEvent.importAndExecute();
    }

    if (dataPoint && event) {
        // measure UserPerceivedTime when the peek is shown using partial event
        dataPoint.markUserPerceivedTime(true /*waitForAnimationFrame*/);

        const ppfViewTime: PastPresentFutureType = getTimeAsPastPresentFutureType({
            start: event.Start,
            end: event.End,
        });
        const isMeeting = isEventAMeeting(event);
        // add custom data
        dataPoint.addCustomData({
            ExperienceType: event.ExperienceType,
            CharmId: event.CharmId,
            PPFViewTime: ppfViewTime,
            isMeeting: isMeeting,
            timeRelevance: getTimeRelevance(event.Start, event.End),
        });
    }

    if (
        isFeatureEnabled('cal-mf-mobility') &&
        isBusiness() &&
        !isConnectedAccount(event.ParentFolderId.mailboxInfo.userIdentity)
    ) {
        lazyFetchMobilityBookends.importAndExecute(event);
    }

    // wait on the promise for fetching full event so that the action datapoint captures right end to end time.
    await fullEventPromise;
}
